import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Dialog, TableAvatar, TableStatus, Typography } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { PetProfileCard } from "../reservation/PetProfileCard";
import { PetPlanDaysSummary } from "../reservation/PetPlanDaysSummary";
import { ReservationPackSelect } from "../reservation/ReservationPackSelect";
import { usePetReservationSummary } from "../reservation/usePetReservationSummary";
import { SIZE_TONE, STATUS_TONE, type PanelPet } from "../panel/panelModel";
import { StayScheduleStrip } from "./StayScheduleStrip";
import { useStayReservationPacks, useUpdateStayReservationPacks } from "./useStayReservationPack";

export interface StayPetDialogProps {
  pet: PanelPet;
  isoDate: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  footer: ReactNode;
  children: ReactNode;
  petProfileAlert?: ReactNode;
  defaultPetCardOpen?: boolean;
  readOnlySchedule?: boolean;
}

/**
 * Casca comum do Check-in e do Acompanhar: mesmo cabeçalho do pet, mesma
 * largura, mesmo rodapé. Cada modal só declara título, rodapé e seções — é o
 * que mantém os dois parecidos sem duplicar marcação.
 */
export function StayPetDialog({ pet, isoDate, open, onOpenChange, title, footer, children, petProfileAlert, defaultPetCardOpen = true, readOnlySchedule = false }: StayPetDialogProps) {
  const { t } = useTranslation("daycareHotel");
  const summary = usePetReservationSummary(pet.petId);
  const { data: customerId } = useCurrentClinicCustomerId();
  const stayPacks = useStayReservationPacks(pet.reservationId);
  const updateStayPacks = useUpdateStayReservationPacks();

  return (
    <Dialog
      className="akuna-dialog--wide akuna-dialog--compact"
      closeLabel={t("panel.close")}
      mode="adaptive"
      open={open}
      onOpenChange={onOpenChange}
      icon={<TableAvatar name={pet.petName} tone={SIZE_TONE[pet.size]} />}
      title={title}
      header={
        <span className="flex items-center gap-2">
          <Typography asChild variant="caption" className="text-muted-foreground">
            <span>
              {[pet.ownerName, pet.size !== "unknown" ? t(`panel.size${pet.size[0].toUpperCase()}${pet.size.slice(1)}`) : null]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </Typography>
          <TableStatus tone="neutral" label={t(`panel.stayType.${pet.nature}`)} />
          <TableStatus tone={STATUS_TONE[pet.status]} label={t(`schedule.status.${pet.status}`)} />
        </span>
      }
      footer={footer}
    >
      <div className="grid gap-4">
        <StayScheduleStrip pet={pet} isoDate={isoDate} readOnly={readOnlySchedule} />
        <PetPlanDaysSummary petId={pet.petId} />
        <ReservationPackSelect
          date={isoDate}
          disabled={readOnlySchedule}
          petId={pet.petId}
          value={stayPacks.data ?? []}
          onChange={(next) => void updateStayPacks.mutateAsync({ reservationId: pet.reservationId, stayPackIds: next })}
        />
        {summary.isLoading ? <Skeleton className="h-52 rounded-[22px]" /> : null}
        {summary.isError ? <Alert variant="danger">{t("petProfile.loadError")}</Alert> : null}
        <PetProfileCard petId={pet.petId} customerId={customerId ?? null} summary={summary.data} alertContent={petProfileAlert} defaultOpen={defaultPetCardOpen} />
        {children}
      </div>
    </Dialog>
  );
}
